/**
 * ContentEngineView - deterministic content composer for sales + engagement.
 *
 * Three-step flow:
 *   1. Pick an asset type (LinkedIn post, cold email, follow-up email, one-pager intro)
 *   2. Pick a target: optionally a persona + optionally a prospect/customer
 *   3. Generate - renders a draft textarea with Copy button
 *
 * All drafts are template-based and personalized from selected records.
 * Deterministic - no network calls. A small honesty pill notes that to get
 * truly bespoke generation, wire the Claude worker via VITE_AGENT_API_URL.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  Copy,
  Check,
  LinkedinIcon,
  Mail,
  FileText,
  Users,
  ChevronDown,
  ChevronUp,
  Gauge,
  Lightbulb,
  Sparkles,
  Loader2,
  AlertTriangle,
  Wand2,
  ShieldCheck,
  BarChart3,
  TrendingUp,
  TrendingDown,
  PlusCircle,
  RotateCcw,
  Zap,
  CalendarClock,
  ArrowLeft,
  MessageSquareText,
  Newspaper,
  Camera,
  X as XDismiss,
  Pencil,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { ContentStudioPanel } from '../components/ContentStudioPanel';
import { LiveSeoEditorPanel } from '../components/LiveSeoEditorPanel';
import { GenerateImageButton } from '../components/GenerateImageButton';
import { C } from '../lib/tokens';
import { bootstrapPeopleIfEmpty, listByCategory } from '../lib/db/people';
import type {
  BrainFact,
  ContentItem,
  ContentItemType,
  Deliverable,
  Person,
} from '../lib/db/types';
import { bootstrapBrainIfEmpty, subscribeBrainFacts } from '../lib/brain/companyBrain';
import { subscribeDeliverables } from '../lib/db/deliverables';
import {
  PREPPED_TEMPLATE_PREFIX,
  deriveRecentWins,
  preparedDrafts,
  type PreppedDraftSpec,
  type PreppedSlot,
} from '../lib/content/preppedDrafts';
import { isAutopilotItem } from '../lib/content/contentAutopilot';
import {
  CONTENT_AUTOPILOT_ID,
  enableAutomation,
  ensureDefaultAutomations,
  pauseAutomation,
  subscribeAutomations,
  type AutomationRecord,
} from '../lib/agent/automations';
import {
  CONTENT_TYPES,
  CONTENT_TEMPLATES,
  contentTypeMeta,
  platformForType,
  summarizeContent,
  type ContentTemplate,
} from '../lib/content/pipeline';
import {
  bootstrapContentItemsIfEmpty,
  createContentItem,
  subscribeContentItems,
} from '../lib/db/contentItems';
import {
  DEFAULT_GROUNDED_FORMAT,
  toolsForType,
  upcomingScheduleDays,
  analyticsTiles,
} from '../lib/content/studioHome';
import { useAuth } from '../lib/auth/AuthProvider';
import { generateDraft, type AssetType } from '../lib/content/drafts';
import { scoreDraft, type Platform } from '../lib/crm/contentScore';
import {
  contentFormats,
  buildGenerationPrompt,
  type ContentFormatSpec,
} from '../lib/content/formats';
import * as argoWorker from '../lib/agent/argoWorker';
import {
  FORMAT_SPECS,
  FORMAT_LABELS,
  buildVoiceProfile,
  generateAsset,
  validateAsset,
  type FormatSpec,
  type GenContext,
  type GeneratedAsset,
} from '../lib/content/generate';
import { resolveMerge } from '../lib/crm/mergeFields';
import { ScoreRewritePanel } from '../components/ScoreRewritePanel';
import { AqiScorePanel } from '../components/AqiScorePanel';
import type { ContentChannel } from '../lib/agent/contentEngine';
import { HistoryShapedPanel } from '../components/HistoryShapedPanel';
import { GovernedCalendarPanel } from '../components/GovernedCalendarPanel';
import {
  createLoggedPost,
  engagementRate,
  toPerformanceProvenanceEntry,
  type LoggedPost,
  type PostMetrics,
} from '../lib/content/postPerformance';
import {
  coldStartWeight,
  deriveSignals,
  diffSignalSets,
  COLD_START_FULL_WEIGHT_POSTS,
  DEFAULT_MIN_WEAK_N,
  type Signal,
  type SignalSet,
} from '../lib/content/whatWorks';
import {
  applySignalUpdate,
  revertProfile,
  type AuthorProfile,
  type ProfileUpdateProposal,
} from '../lib/content/authorProfile';
import {
  MIN_AUTO_QUEUE_STREAK,
  buildWeeklyMission,
  canAutoQueue,
  runWeeklyLoop,
  verifyOutcome,
  type LoopResult,
  type LoopStep,
  type ReplyRateOutcome,
  type WeeklyMission,
} from '../lib/content/learningLoop';

interface AssetMeta {
  id: AssetType;
  name: string;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
}

const ASSET_TYPES: AssetMeta[] = [
  {
    id: 'linkedin',
    name: 'LinkedIn Post',
    description: 'Thought leadership + engagement hook.',
    icon: LinkedinIcon,
  },
  {
    id: 'cold-email',
    name: 'Cold Email',
    description: 'Subject + 120-word pitch with one CTA.',
    icon: Mail,
  },
  {
    id: 'follow-up-email',
    name: 'Follow-up Email',
    description: 'References a prior conversation, asks for next step.',
    icon: Mail,
  },
  {
    id: 'one-pager',
    name: 'One-Pager Intro',
    description: 'Problem -> promise -> proof structure.',
    icon: FileText,
  },
];

/** Icon per studio content type — the home picker + templates gallery. */
const STUDIO_TYPE_ICONS: Record<ContentItemType, LucideIcon> = {
  'linkedin-post': LinkedinIcon,
  'x-thread': MessageSquareText,
  'x-post': MessageSquareText,
  blog: Newspaper,
  email: Mail,
  'ig-caption': Camera,
};

/** Composer asset type → the argoiq-engine's AQI channel vocabulary. */
const AQI_CHANNEL_FOR_ASSET: Record<AssetType, ContentChannel> = {
  linkedin: 'linkedin',
  'cold-email': 'email_newsletter',
  'follow-up-email': 'email_newsletter',
  'one-pager': 'aeo_blog',
};

/** Grade → accent color, one place so the chip and breakdown never drift. */
const GRADE_COLOR: Record<'A' | 'B' | 'C' | 'D', string> = {
  A: C.green,
  B: C.blueElectric,
  C: C.orange,
  D: C.orangeWarm,
};

/**
 * Compact best-practice score for a visible draft: a grade + total chip that
 * expands into the per-dimension breakdown and rewrite suggestions. Thin
 * wrapper over the pure, deterministic scorer in lib/crm/contentScore.
 */
function DraftScoreCard({ text, platform }: { text: string; platform: Platform }) {
  const [open, setOpen] = useState(false);
  const score = useMemo(() => scoreDraft(text, { platform }), [text, platform]);
  const color = GRADE_COLOR[score.grade];

  return (
    <GlassPanel style={{ padding: 12 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="glass-row"
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          alignItems: 'center',
          gap: 10,
          justifyContent: 'flex-start',
        }}
        aria-expanded={open}
      >
        <Gauge size={16} style={{ color, flexShrink: 0 }} />
        <span className="t-card-title" style={{ color: C.text, flex: 1, textAlign: 'left' }}>
          Best-practice score
        </span>
        <span
          className="glass-pill"
          style={{
            fontSize: 11,
            padding: '3px 10px',
            background: `${color}22`,
            color,
            boxShadow: `inset 0 0 0 1px ${color}55`,
            fontWeight: 600,
          }}
        >
          {score.grade} · {score.total}/100
        </span>
        {open ? (
          <ChevronUp size={14} style={{ color: C.textMuted }} />
        ) : (
          <ChevronDown size={14} style={{ color: C.textMuted }} />
        )}
      </button>

      {open && (
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            {score.dimensions.map((d) => {
              const pct = d.max > 0 ? d.score / d.max : 0;
              const barColor = pct >= 0.6 ? C.green : pct >= 0.35 ? C.orange : C.orangeWarm;
              return (
                <div key={d.key} style={{ display: 'grid', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                      {d.label}
                    </span>
                    <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
                      {d.score}/{d.max}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: 'rgba(255,255,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round(pct * 100)}%`,
                        height: '100%',
                        borderRadius: 2,
                        background: barColor,
                      }}
                    />
                  </div>
                  <div className="t-meta" style={{ color: C.textDim }}>
                    {d.note}
                  </div>
                </div>
              );
            })}
          </div>

          {score.suggestions.length > 0 && (
            <div
              style={{
                borderTop: '1px solid rgba(255,255,255,0.08)',
                paddingTop: 10,
                display: 'grid',
                gap: 6,
              }}
            >
              <div className="t-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lightbulb size={12} style={{ color: C.orange }} />
                Rewrite tips
              </div>
              {score.suggestions.map((s) => (
                <div key={s} className="t-meta" style={{ color: C.textMuted }}>
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  );
}

/** One generated format's lifecycle in the multi-format run. */
interface FormatResult {
  format: ContentFormatSpec;
  status: 'loading' | 'done' | 'error';
  text: string;
  error?: string;
}

/**
 * One generated format as a card: the Claude-written text plus an AUTOMATIC
 * best-practice score (grade + total + top suggestions) from the shipped
 * scorer in lib/crm/contentScore — reused, never reimplemented.
 */
function FormatResultCard({ result }: { result: FormatResult }) {
  const { format, status, text, error } = result;
  const [copied, setCopied] = useState(false);
  const score = useMemo(
    () => (status === 'done' ? scoreDraft(text, { platform: format.scorePlatform }) : null),
    [status, text, format.scorePlatform],
  );
  const color = score ? GRADE_COLOR[score.grade] : C.textMuted;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — non-fatal.
    }
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 10, alignContent: 'start' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-card-title" style={{ color: C.text }}>
            {format.label}
          </div>
          <div className="t-meta" style={{ margin: '2px 0 0', color: C.textDim }}>
            {format.platform} · {format.description}
          </div>
        </div>
        {status === 'loading' && (
          <Loader2 size={16} className="animate-spin" style={{ color: C.textMuted }} />
        )}
        {score && (
          <span
            className="glass-pill"
            title="Auto-scored with the shipped best-practice scorer"
            style={{
              fontSize: 11,
              padding: '3px 10px',
              background: `${color}22`,
              color,
              boxShadow: `inset 0 0 0 1px ${color}55`,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {score.grade} · {score.total}/100
          </span>
        )}
        {status === 'done' && (
          <button
            onClick={handleCopy}
            className="glass-btn"
            style={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {copied ? (
              <Check size={12} style={{ color: C.green }} />
            ) : (
              <Copy size={12} style={{ color: C.textMuted }} />
            )}
          </button>
        )}
      </div>

      {status === 'error' && (
        <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', gap: 6 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{error || 'Generation failed.'}</span>
        </div>
      )}

      {status === 'done' && (
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: 12,
            lineHeight: 1.5,
            color: C.text,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10,
            padding: 12,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {text}
        </pre>
      )}

      {score && score.suggestions.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          <div className="t-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lightbulb size={11} style={{ color: C.orange }} />
            Top rewrite tips
          </div>
          {score.suggestions.slice(0, 2).map((s) => (
            <div key={s} className="t-meta" style={{ color: C.textMuted }}>
              {s}
            </div>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}

/** Lifecycle of one CRM-grounded, per-format generation run. */
interface GroundedState {
  status: 'loading' | 'done' | 'error';
  asset?: GeneratedAsset;
  validation?: { ok: boolean; violations: string[] };
  error?: string;
}

/**
 * The active format's result area: empty / skeleton / error / done states,
 * all rendered from `generateAsset`'s returned provenance + `validateAsset`
 * result — the view assembles NO content strings of its own.
 */
function GroundedResultCard({
  state,
  spec,
}: {
  state: GroundedState | undefined;
  spec: FormatSpec;
}) {
  const [copied, setCopied] = useState(false);
  const label = FORMAT_LABELS[spec.format] ?? spec.format;

  const handleCopy = async () => {
    if (!state?.asset) return;
    try {
      await navigator.clipboard.writeText(state.asset.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — non-fatal.
    }
  };

  // Empty state: no run yet for this format.
  if (!state) {
    return (
      <div
        className="t-meta"
        style={{
          color: C.textMuted,
          border: '1px dashed rgba(255,255,255,0.15)',
          borderRadius: 10,
          padding: '18px 14px',
          textAlign: 'center',
        }}
      >
        No {label.toLowerCase()} draft yet — write a brief above and hit Generate.
      </div>
    );
  }

  // Skeleton while the worker (or fallback) is composing.
  if (state.status === 'loading') {
    return (
      <div style={{ display: 'grid', gap: 8 }} aria-busy="true">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader2 size={14} className="animate-spin" style={{ color: C.textMuted }} />
          <span className="t-meta" style={{ color: C.textMuted }}>
            Generating {label.toLowerCase()}…
          </span>
        </div>
        {[92, 100, 78].map((w, i) => (
          <div
            key={i}
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 6,
              background: 'rgba(255,255,255,0.07)',
            }}
          />
        ))}
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', gap: 6 }}>
        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{state.error || 'Generation failed.'}</span>
      </div>
    );
  }

  const asset = state.asset;
  const validation = state.validation;
  if (!asset) return null;
  const vColor = validation?.ok ? C.green : C.orange;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {/* Provenance + validation chips — straight from the returned data. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span
          className="glass-pill"
          title={`model: ${asset.provenance.model}`}
          style={{
            fontSize: 11,
            padding: '3px 10px',
            color: asset.provenance.usedWorker ? C.green : C.textMuted,
            background: asset.provenance.usedWorker ? `${C.green}22` : 'rgba(255,255,255,0.06)',
          }}
        >
          {asset.provenance.usedWorker ? 'live worker' : 'deterministic fallback'}
        </span>
        <span className="glass-pill" style={{ fontSize: 11, padding: '3px 10px', color: C.textDim }}>
          prompt #{asset.provenance.promptHash}
        </span>
        {validation && (
          <span
            className="glass-pill"
            style={{
              fontSize: 11,
              padding: '3px 10px',
              color: vColor,
              background: `${vColor}22`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ShieldCheck size={11} />
            {validation.ok
              ? 'passes format rules'
              : `${validation.violations.length} format issue${validation.violations.length === 1 ? '' : 's'}`}
          </span>
        )}
        <button
          onClick={handleCopy}
          className="glass-btn"
          style={{
            marginLeft: 'auto',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {copied ? (
            <Check size={12} style={{ color: C.green }} />
          ) : (
            <Copy size={12} style={{ color: C.textMuted }} />
          )}
        </button>
      </div>

      {validation && !validation.ok && (
        <div style={{ display: 'grid', gap: 3 }}>
          {validation.violations.map((v) => (
            <div key={v} className="t-meta" style={{ color: C.orange }}>
              · {v}
            </div>
          ))}
        </div>
      )}

      <pre
        style={{
          margin: 0,
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          fontSize: 12,
          lineHeight: 1.5,
          color: C.text,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10,
          padding: 12,
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {asset.text}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post Performance Ledger + What-Works signals — thin UI over the PURE
// engines in lib/content/postPerformance + lib/content/whatWorks. Records
// live in view memory for this session (no store writes by design).
// ---------------------------------------------------------------------------

/** Signal strength → accent color, one place so cards never drift. */
const STRENGTH_COLOR: Record<Signal['strength'], string> = {
  strong: C.green,
  weak: C.orange,
  none: C.textMuted,
};

const PERF_INPUT_STYLE: CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '6px 8px',
  color: C.text,
  fontSize: 12,
  outline: 'none',
  width: '100%',
};

/** Parse a metric field: blank/garbage is honestly 0, never NaN. */
function metricNum(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The LOG side of the loop: paste (or pull in) the text you actually
 * published, enter its observed metrics, and log it. Every logged row keeps
 * its views/likes/replies inline-editable — edits rebuild the record through
 * `createLoggedPost` so derived features always match the text.
 */
function PostPerformancePanel({
  posts,
  onLog,
  onUpdateMetrics,
  currentDraft,
}: {
  posts: LoggedPost[];
  onLog: (post: LoggedPost) => void;
  onUpdateMetrics: (id: string, metrics: PostMetrics) => void;
  currentDraft: string;
}) {
  const [text, setText] = useState('');
  const [format, setFormat] = useState<string>('linkedinPost');
  const [postedAtHour, setPostedAtHour] = useState(9);
  const [views, setViews] = useState('');
  const [likes, setLikes] = useState('');
  const [replies, setReplies] = useState('');
  const [bookmarks, setBookmarks] = useState('');
  const [followerDelta, setFollowerDelta] = useState('');

  const canLog = text.trim().length > 0;

  const handleLog = () => {
    if (!canLog) return;
    const post = createLoggedPost({
      id: `post-${Date.now().toString(36)}-${posts.length + 1}`,
      text: text.trim(),
      format,
      postedAtHour,
      metrics: {
        views: metricNum(views),
        likes: metricNum(likes),
        replies: metricNum(replies),
        bookmarks: metricNum(bookmarks),
        followerDelta: metricNum(followerDelta),
      },
    });
    onLog(post);
    setText('');
    setViews('');
    setLikes('');
    setReplies('');
    setBookmarks('');
    setFollowerDelta('');
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="t-eyebrow" style={{ flex: 1 }}>
            Log a published post
          </span>
          {currentDraft.trim().length > 0 && (
            <button
              className="glass-btn"
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => setText(currentDraft)}
            >
              Use current draft
            </button>
          )}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the post exactly as it was published — hook style, topic, and length are derived from this text."
          style={{
            ...PERF_INPUT_STYLE,
            minHeight: 80,
            fontFamily: 'monospace',
            resize: 'vertical',
          }}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 8,
          }}
        >
          <label className="t-meta" style={{ display: 'grid', gap: 4 }}>
            Format
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              style={PERF_INPUT_STYLE}
            >
              {Object.keys(FORMAT_SPECS).map((f) => (
                <option key={f} value={f}>
                  {FORMAT_LABELS[f] ?? f}
                </option>
              ))}
            </select>
          </label>
          <label className="t-meta" style={{ display: 'grid', gap: 4 }}>
            Posted hour
            <select
              value={postedAtHour}
              onChange={(e) => setPostedAtHour(Number(e.target.value))}
              style={PERF_INPUT_STYLE}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </label>
          {(
            [
              ['Views', views, setViews],
              ['Likes', likes, setLikes],
              ['Replies', replies, setReplies],
              ['Bookmarks', bookmarks, setBookmarks],
              ['Follower Δ', followerDelta, setFollowerDelta],
            ] as const
          ).map(([label, value, setter]) => (
            <label key={label} className="t-meta" style={{ display: 'grid', gap: 4 }}>
              {label}
              <input
                type="number"
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder="0"
                style={PERF_INPUT_STYLE}
              />
            </label>
          ))}
        </div>
        <button
          onClick={handleLog}
          disabled={!canLog}
          className="glass-btn glass-btn--primary"
          style={{
            justifySelf: 'start',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: canLog ? 1 : 0.5,
            cursor: canLog ? 'pointer' : 'default',
          }}
        >
          <PlusCircle size={14} />
          Log post
        </button>
      </div>

      {posts.length === 0 ? (
        <div
          className="t-meta"
          style={{
            color: C.textMuted,
            border: '1px dashed rgba(255,255,255,0.15)',
            borderRadius: 10,
            padding: '14px 12px',
          }}
        >
          No posts logged yet. Log real published posts with their observed metrics — the
          What-Works signals are derived only from what you log, never fabricated.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="t-eyebrow">Performance ledger · {posts.length} logged</div>
          {[...posts].reverse().map((p) => {
            const rate = engagementRate(p.metrics);
            const prov = toPerformanceProvenanceEntry(p);
            return (
              <div
                key={p.id}
                style={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  padding: 10,
                  display: 'grid',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span
                    className="t-card-title"
                    style={{
                      fontSize: 12,
                      color: C.text,
                      flex: 1,
                      minWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={p.text}
                  >
                    {p.text.split('\n')[0]}
                  </span>
                  {[
                    FORMAT_LABELS[p.format] ?? p.format,
                    `${p.hookStyle} hook`,
                    p.topic,
                    `${p.wordCount}w`,
                    `${String(p.postedAtHour).padStart(2, '0')}:00`,
                  ].map((chip) => (
                    <span
                      key={chip}
                      className="glass-pill"
                      style={{ fontSize: 10, padding: '2px 8px', color: C.textMuted }}
                    >
                      {chip}
                    </span>
                  ))}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                    gap: 8,
                    alignItems: 'end',
                  }}
                >
                  {(
                    [
                      ['Views', 'views'],
                      ['Likes', 'likes'],
                      ['Replies', 'replies'],
                    ] as const
                  ).map(([label, key]) => (
                    <label key={key} className="t-meta" style={{ display: 'grid', gap: 3 }}>
                      {label}
                      <input
                        type="number"
                        value={p.metrics[key]}
                        onChange={(e) =>
                          onUpdateMetrics(p.id, {
                            ...p.metrics,
                            [key]: metricNum(e.target.value),
                          })
                        }
                        style={{ ...PERF_INPUT_STYLE, padding: '4px 8px' }}
                      />
                    </label>
                  ))}
                  <span className="t-meta" style={{ color: C.textDim }}>
                    engagement {(rate * 100).toFixed(2)}%
                  </span>
                </div>
                <div className="t-meta" style={{ color: C.textDim, fontSize: 10 }}>
                  provenance {prov.digest} · confidence{' '}
                  {(prov.confidence.score * 100).toFixed(0)}% (view-count based)
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * The INSIGHTS side of the loop: `deriveSignals` output rendered as evidence
 * cards — each with its lift, honest sample size, and the exact post ids
 * backing it — plus the transparent cold-start weight and weak→strong
 * upgrade notices from `diffSignalSets`.
 */
function WhatWorksCards({ posts }: { posts: LoggedPost[] }) {
  const signalSet = useMemo(() => deriveSignals(posts), [posts]);
  // Diff against the PREVIOUS derivation so upgrades surface the moment new
  // logs push a pattern past the strong threshold.
  const prevRef = useRef<SignalSet | null>(null);
  const prevSet = prevRef.current;
  const changes = useMemo(
    () => (prevSet && prevSet !== signalSet ? diffSignalSets(prevSet, signalSet) : null),
    [prevSet, signalSet],
  );
  useEffect(() => {
    prevRef.current = signalSet;
  }, [signalSet]);

  const weight = coldStartWeight(signalSet.totalPosts);
  const claimed = signalSet.signals.filter((s) => s.strength !== 'none');
  const suppressed = signalSet.signals.length - claimed.length;

  if (signalSet.totalPosts === 0) {
    return (
      <div
        className="t-meta"
        style={{
          color: C.textMuted,
          border: '1px dashed rgba(255,255,255,0.15)',
          borderRadius: 10,
          padding: '14px 12px',
        }}
      >
        No performance data yet — log published posts in the Log tab. Signals are only ever
        derived from your real logged posts.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Transparent personal-signal weight (cold start). */}
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="t-eyebrow">Personal signal weight</span>
          <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
            {Math.round(weight * 100)}% · {signalSet.totalPosts} post
            {signalSet.totalPosts === 1 ? '' : 's'} logged (full weight at{' '}
            {COLD_START_FULL_WEIGHT_POSTS})
          </span>
        </div>
        <div
          style={{
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.round(weight * 100)}%`,
              height: '100%',
              borderRadius: 2,
              background: weight >= 1 ? C.green : C.blueElectric,
            }}
          />
        </div>
      </div>

      {/* Upgrade / downgrade notices from the latest re-derivation. */}
      {changes && (changes.upgraded.length > 0 || changes.downgraded.length > 0) && (
        <div style={{ display: 'grid', gap: 4 }}>
          {changes.upgraded.map((s) => {
            const before = prevSet?.signals.find(
              (x) => x.dimension === s.dimension && x.value === s.value,
            );
            return (
              <div
                key={`up-${s.dimension}-${s.value}`}
                className="t-meta"
                style={{ color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <TrendingUp size={12} />
                {s.value} ({s.dimension}) upgraded {before?.strength ?? 'weak'} → {s.strength}
                {before ? ` · n ${before.sampleSize} → ${s.sampleSize}` : ` · n=${s.sampleSize}`}
              </div>
            );
          })}
          {changes.downgraded.map((s) => (
            <div
              key={`down-${s.dimension}-${s.value}`}
              className="t-meta"
              style={{ color: C.orangeWarm, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <TrendingDown size={12} />
              {s.value} ({s.dimension}) downgraded to {s.strength} · n={s.sampleSize}
            </div>
          ))}
        </div>
      )}

      {claimed.length === 0 ? (
        <div className="t-meta" style={{ color: C.textMuted }}>
          Not enough repeated patterns yet to claim any signal — every pattern so far has fewer
          than {DEFAULT_MIN_WEAK_N} posts behind it. Keep logging.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}
        >
          {claimed.map((s) => {
            const color = STRENGTH_COLOR[s.strength];
            return (
              <div
                key={`${s.dimension}-${s.value}`}
                style={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  padding: 10,
                  display: 'grid',
                  gap: 6,
                  alignContent: 'start',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="t-card-title" style={{ fontSize: 12, color: C.text, flex: 1 }}>
                    {s.value}
                  </span>
                  <span
                    className="glass-pill"
                    style={{
                      fontSize: 10,
                      padding: '2px 8px',
                      background: `${color}22`,
                      color,
                      boxShadow: `inset 0 0 0 1px ${color}55`,
                      fontWeight: 600,
                    }}
                  >
                    {s.strength} · n={s.sampleSize}
                  </span>
                </div>
                <div className="t-meta" style={{ color: C.textDim }}>
                  {s.dimension} · {s.lift >= 1 ? '+' : ''}
                  {Math.round((s.lift - 1) * 100)}% vs your average ({s.lift}× lift)
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {s.evidencePostIds.map((id) => (
                    <span
                      key={id}
                      className="glass-pill"
                      title={`Evidence post ${id}`}
                      style={{ fontSize: 9, padding: '1px 6px', color: C.textMuted }}
                    >
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {suppressed > 0 && (
        <div className="t-meta" style={{ color: C.textMuted }}>
          {suppressed} candidate pattern{suppressed === 1 ? '' : 's'} hidden — fewer than{' '}
          {DEFAULT_MIN_WEAK_N} posts each. Sample-size honesty: no signal is claimed from n=1–2.
        </div>
      )}
    </div>
  );
}

/** Canonical render order of the loop's step chips. */
const LOOP_STEPS: LoopStep[] = [
  'logged',
  'learned',
  'proposed',
  'approved',
  'generated',
  'scored',
  'scheduled',
];

/** Verdict → accent color for the outcome badge. */
const VERDICT_COLOR: Record<ReplyRateOutcome['verdict'], string> = {
  VERIFIED: C.green,
  MISSED: C.orangeWarm,
  INCONCLUSIVE: C.orange,
};

/**
 * Governed Weekly Learning Loop — the operator's Claude ritual as one
 * ledger-governed mission over the PURE engines in lib/content/learningLoop
 * + lib/content/authorProfile:
 *
 *   Run weekly loop  → runWeeklyLoop (REAL worker when configured,
 *                      deterministic fallback otherwise) with live LoopStep
 *                      progress chips;
 *   Approve learning → applySignalUpdate on the pending proposeProfileUpdate
 *                      proposal (reject keeps the current voice) — learning
 *                      is human-approved, never silent drift;
 *   Revert voice     → revertProfile over the kept version history (Time
 *                      Machine for voice);
 *   Autonomy badge   → canAutoQueue over the real approval history — the
 *                      earned-autonomy ladder, blast-radius bounded;
 *   Verify outcome   → verifyOutcome replays the logged ledger against the
 *                      sealed-shape contract (reply rate up 10% / 4 weeks).
 */
function WeeklyLoopPanel({
  posts,
  ideaSeed,
  workerReady,
}: {
  posts: LoggedPost[];
  ideaSeed: string;
  workerReady: boolean;
}) {
  // Every profile version that has EVER been current, oldest → newest.
  // The live profile is the last entry; earlier entries feed revertProfile.
  const [versions, setVersions] = useState<AuthorProfile[]>([
    {
      authorId: 'operator',
      version: 1,
      signalSet: { signals: [], totalPosts: 0, computedAt: 0 },
      bannedPhrases: [],
      toneNotes: [],
      updatedAt: 0,
    },
  ]);
  const profile = versions[versions.length - 1];

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<LoopResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'approved' | 'rejected' | null>(null);
  const [mission, setMission] = useState<WeeklyMission | null>(null);
  const [verdict, setVerdict] = useState<ReplyRateOutcome | null>(null);
  // Honest earn-up history: one row per human decision on a loop run.
  const [approvals, setApprovals] = useState<
    { scored: number; approvedUnedited: boolean }[]
  >([]);

  const autoQueueEarned = canAutoQueue(approvals, { minStreak: MIN_AUTO_QUEUE_STREAK });
  const proposal = (result?.proposal as ProfileUpdateProposal | undefined) ?? null;
  const meanScore = result
    ? Math.round(
        result.drafts.reduce((s, d) => s + d.score, 0) / Math.max(1, result.drafts.length),
      )
    : 0;

  const stepsDone = useMemo(() => {
    const done = new Set<LoopStep>(result?.steps ?? []);
    if (decision === 'approved') done.add('approved');
    if (mission) done.add('scheduled');
    return done;
  }, [result, decision, mission]);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setDecision(null);
    setMission(null);
    setVerdict(null);
    try {
      // Real worker seam when configured; runWeeklyLoop degrades to its
      // deterministic fallback on absence/failure — the loop never dies.
      const worker = workerReady
        ? {
            run: async (p: string) =>
              (
                await argoWorker.runAgent(
                  {
                    id: 'weekly-learning-loop',
                    name: 'Weekly Learning Loop',
                    template: 'content-writer',
                    persona: 'weekly ritual writer',
                  },
                  { prompt: p },
                )
              ).text,
          }
        : undefined;
      const ideas = [
        ideaSeed.trim() || 'what we learned shipping governed agents this week',
      ];
      const out = await runWeeklyLoop({ profile, loggedPosts: posts, ideas, worker });
      setResult(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Weekly loop failed.');
    } finally {
      setRunning(false);
    }
  };

  const handleApprove = () => {
    if (!proposal || decision) return;
    const { next } = applySignalUpdate(profile, proposal.nextSignalSet);
    setVersions((prev) => [...prev, next]);
    setDecision('approved');
    setApprovals((prev) => [...prev, { scored: meanScore, approvedUnedited: true }]);
    // Approval closes the loop into the scheduler: build the mission entry.
    setMission(
      buildWeeklyMission({
        profile: next,
        loggedPosts: posts,
        ideas: [ideaSeed.trim() || 'what we learned shipping governed agents this week'],
      }),
    );
  };

  const handleReject = () => {
    if (!proposal || decision) return;
    setDecision('rejected');
    // A rejection is an honest streak reset — autonomy must be re-earned.
    setApprovals((prev) => [...prev, { scored: meanScore, approvedUnedited: false }]);
  };

  const handleRevert = (toVersion: number) => {
    try {
      const restored = revertProfile(versions, toVersion);
      setVersions((prev) => [...prev, restored]);
    } catch {
      // Unknown version — the buttons only offer real ones, so unreachable.
    }
  };

  // Distinct earlier versions the Time Machine can restore.
  const revertTargets = [...new Set(versions.map((v) => v.version))].filter(
    (v) => v !== profile.version,
  );

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <CalendarClock size={16} style={{ color: C.green }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Weekly learning loop
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          log → learn → approve → generate → score → schedule · governed &amp; reversible
        </span>
        <span
          className="glass-pill"
          title="Current approved voice version"
          style={{ marginLeft: 'auto', fontSize: 11, padding: '3px 10px', color: C.blueElectric }}
        >
          voice v{profile.version}
        </span>
        <span
          className="glass-pill"
          title={
            autoQueueEarned
              ? `Earned: last ${MIN_AUTO_QUEUE_STREAK}+ approvals were high-scoring and unedited`
              : `Locked: needs ${MIN_AUTO_QUEUE_STREAK} consecutive high-score unedited approvals (${approvals.length} decision${approvals.length === 1 ? '' : 's'} so far)`
          }
          style={{
            fontSize: 11,
            padding: '3px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: autoQueueEarned ? C.green : C.textMuted,
            background: autoQueueEarned ? `${C.green}18` : 'rgba(255,255,255,0.05)',
            boxShadow: autoQueueEarned ? `inset 0 0 0 1px ${C.green}55` : 'none',
          }}
        >
          <Zap size={11} />
          {autoQueueEarned ? 'auto-queue earned' : 'human-approved'}
        </span>
      </div>

      {/* LoopStep progress chips — honest: only completed steps light up. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {LOOP_STEPS.map((step, i) => {
          const done = stepsDone.has(step);
          return (
            <span
              key={step}
              className="glass-pill"
              style={{
                fontSize: 11,
                padding: '3px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: done ? C.green : C.textMuted,
                background: done ? `${C.green}18` : 'rgba(255,255,255,0.05)',
                boxShadow: done ? `inset 0 0 0 1px ${C.green}55` : 'none',
              }}
            >
              {done ? <Check size={11} /> : <span style={{ opacity: 0.6 }}>{i + 1}·</span>}
              {step}
            </span>
          );
        })}
        {running && <Loader2 size={14} className="animate-spin" style={{ color: C.textMuted }} />}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => void handleRun()}
          disabled={running}
          className="glass-btn glass-btn--primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: running ? 0.5 : 1,
            cursor: running ? 'default' : 'pointer',
          }}
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? 'Running…' : 'Run weekly loop'}
        </button>
        {result && (
          <button
            onClick={() => setVerdict(verifyOutcome(result.contract, posts))}
            className="glass-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <ShieldCheck size={14} />
            Verify outcome
          </button>
        )}
        {revertTargets.map((v) => (
          <button
            key={v}
            onClick={() => handleRevert(v)}
            className="glass-btn"
            title={`Restore voice v${v} exactly — Time Machine for voice`}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={13} />
            Revert voice to v{v}
          </button>
        ))}
      </div>

      {!workerReady && (
        <div className="t-meta" style={{ color: C.textMuted }}>
          Worker not configured — the loop runs on the deterministic fallback (labelled
          honestly below). Set VITE_AGENT_API_URL for live Claude drafting.
        </div>
      )}

      {error && (
        <div className="t-meta" style={{ color: C.orangeWarm, display: 'flex', gap: 6 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{error}</span>
        </div>
      )}

      {/* Pending learning proposal — human-approved, vetoable, never silent. */}
      {proposal && (
        <div
          style={{
            border: `1px solid ${decision ? 'rgba(255,255,255,0.1)' : `${C.orange}55`}`,
            borderRadius: 10,
            padding: 12,
            display: 'grid',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="t-card-title" style={{ color: C.text }}>
              Learning proposal
            </span>
            <span
              className="glass-pill"
              style={{
                fontSize: 10,
                padding: '2px 8px',
                color:
                  decision === 'approved'
                    ? C.green
                    : decision === 'rejected'
                      ? C.orangeWarm
                      : C.orange,
              }}
            >
              {decision ?? 'pending approval'}
            </span>
            <span className="t-meta" style={{ marginLeft: 'auto', color: C.textDim }}>
              {proposal.id}
            </span>
          </div>
          <div className="t-meta" style={{ color: C.textMuted, fontFamily: 'monospace' }}>
            {proposal.actionDiff}
          </div>
          {proposal.evidence.slice(0, 5).map((e) => (
            <div key={e} className="t-meta" style={{ color: C.textDim }}>
              {e}
            </div>
          ))}
          {!decision && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleApprove}
                className="glass-btn glass-btn--primary"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Check size={13} />
                Approve learning
              </button>
              <button onClick={handleReject} className="glass-btn">
                Reject — keep v{profile.version}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scored drafts from the run, with honest generation provenance. */}
      {result && result.drafts.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="t-eyebrow">
            Drafts · {result.source === 'worker' ? 'live worker' : 'deterministic fallback'}
          </div>
          {result.drafts.map((d, i) => (
            <div
              key={i}
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10,
                padding: 10,
                display: 'grid',
                gap: 6,
              }}
            >
              <span
                className="glass-pill"
                style={{ fontSize: 10, padding: '2px 8px', color: C.blueElectric, width: 'fit-content' }}
              >
                score {d.score}/100
              </span>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: C.text,
                  maxHeight: 160,
                  overflowY: 'auto',
                }}
              >
                {d.text}
              </pre>
            </div>
          ))}
        </div>
      )}

      {/* The mission entry the approval queued for the scheduler. */}
      {mission && (
        <div className="t-meta" style={{ color: C.textMuted, display: 'flex', gap: 6 }}>
          <CalendarClock size={13} style={{ color: C.green, flexShrink: 0, marginTop: 1 }} />
          <span>
            Scheduled mission “{mission.title}” — {mission.cadence}, next window in{' '}
            {Math.round(mission.intervalMs / (24 * 60 * 60 * 1000))} days ·{' '}
            {mission.proposalRef}
          </span>
        </div>
      )}

      {/* Outcome verdict — the contract's after-answer, never assumed. */}
      {verdict && (
        <div
          className="t-meta"
          style={{ color: VERDICT_COLOR[verdict.verdict], display: 'flex', gap: 6, flexWrap: 'wrap' }}
        >
          <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Outcome {verdict.verdict}: predicted reply rate {verdict.predicted} · measured{' '}
            {verdict.actual}. {verdict.evidence[0]}
          </span>
        </div>
      )}
    </GlassPanel>
  );
}

/**
 * @param initialContentType — when set (e.g. the Blog/SEO nav deep-link),
 *   the view opens directly in that per-type workspace, skipping the
 *   generic type picker. The breadcrumb "back to all types" still returns
 *   to the picker so the operator is never trapped.
 */
export function ContentEngineView({
  initialContentType = null,
}: { initialContentType?: ContentItemType | null } = {}) {
  const { user } = useAuth();
  // Sign email drafts as the signed-in operator (first name), matching the
  // AgentsView actor pattern. Falls back to an obvious fill-in placeholder
  // rather than impersonating a teammate when we don't know the name.
  const senderName = user?.displayName?.split(' ')[0] || '[Your name]';
  const [assetType, setAssetType] = useState<AssetType>('linkedin');
  const [personas, setPersonas] = useState<Person[]>([]);
  const [targets, setTargets] = useState<Person[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [copied, setCopied] = useState(false);

  // -------------------------------------------------------------------
  // Decluttered flow: the view OPENS on a clean type picker + templates
  // gallery (home). Picking a type (or template) opens that type's
  // focused workspace; every dense tool lives BEHIND the chosen type.
  // -------------------------------------------------------------------
  const [activeType, setActiveType] = useState<ContentItemType | null>(initialContentType);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);

  useEffect(() => {
    bootstrapContentItemsIfEmpty().catch(() => undefined);
    return subscribeContentItems(setContentItems);
  }, []);

  const homeAnalytics = useMemo(
    () => analyticsTiles(summarizeContent(contentItems)),
    [contentItems],
  );
  const homeSummary = useMemo(() => summarizeContent(contentItems), [contentItems]);
  const scheduleDays = useMemo(
    () => upcomingScheduleDays(contentItems, Date.now(), 7),
    [contentItems],
  );

  /** Enter a type's workspace, landing grounded generation on its format. */
  const openType = (type: ContentItemType) => {
    setActiveType(type);
    setActiveFormat(DEFAULT_GROUNDED_FORMAT[type]);
  };

  /** One click on a template: mint a scored Draft, then open its workspace. */
  const useHomeTemplate = async (tpl: ContentTemplate) => {
    await createContentItem({
      title: `${tpl.name} — ${contentTypeMeta(tpl.type).label}`,
      type: tpl.type,
      status: 'draft',
      body: tpl.body,
      score: scoreDraft(tpl.body.trim(), { platform: platformForType(tpl.type) }).total,
      templateId: tpl.id,
    });
    openType(tpl.type);
  };

  // -------------------------------------------------------------------
  // THE INVERSION MANDATE: the landing opens on Argo-prepped drafts —
  // finished work derived from the Company Brain + real records by the
  // PURE generator in lib/content/preppedDrafts — with the old type
  // picker demoted to a '+ New' secondary action. Empty Brain → the
  // picker fallback with an honest "set up your Brain" hint.
  // -------------------------------------------------------------------
  const [brainFacts, setBrainFacts] = useState<BrainFact[]>([]);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [dismissedSlots, setDismissedSlots] = useState<PreppedSlot[]>([]);
  const [preppedVariants, setPreppedVariants] = useState<
    Partial<Record<PreppedSlot, number>>
  >({});
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    bootstrapBrainIfEmpty().catch(() => undefined);
    const unsubFacts = subscribeBrainFacts(setBrainFacts);
    const unsubDeliverables = subscribeDeliverables(setDeliverables);
    return () => {
      unsubFacts();
      unsubDeliverables();
    };
  }, []);

  const preppedPeople = useMemo(() => [...personas, ...targets], [personas, targets]);
  const prepped = useMemo(
    () =>
      preparedDrafts(
        brainFacts,
        preppedPeople,
        deriveRecentWins(deliverables, preppedPeople),
        contentItems,
        preppedVariants,
      ).filter((d) => !dismissedSlots.includes(d.slot)),
    [brainFacts, preppedPeople, deliverables, contentItems, preppedVariants, dismissedSlots],
  );

  /**
   * [Open & score] / [Edit]: mint the prepped draft as a REAL content item
   * (status 'draft' — publishing stays behind the born-scored gate) and land
   * in that type's workspace. Open-&-score stamps the shipped scorer's
   * number; Edit leaves it honestly unscored until the operator saves.
   */
  const openPrepped = async (spec: PreppedDraftSpec, opts: { score: boolean }) => {
    await createContentItem({
      title: spec.title,
      type: spec.type,
      status: 'draft',
      body: spec.body,
      score: opts.score
        ? scoreDraft(spec.body.trim(), { platform: platformForType(spec.type) }).total
        : null,
      templateId: `${PREPPED_TEMPLATE_PREFIX}${spec.slot}`,
    });
    openType(spec.type);
  };

  /** [Regenerate]: re-derive ONE slot deterministically (variant rotation). */
  const regeneratePrepped = (slot: PreppedSlot) =>
    setPreppedVariants((prev) => ({ ...prev, [slot]: (prev[slot] ?? 0) + 1 }));

  /** [Dismiss]: hide a slot for this session — records stay untouched. */
  const dismissPrepped = (slot: PreppedSlot) =>
    setDismissedSlots((prev) => (prev.includes(slot) ? prev : [...prev, slot]));

  // -------------------------------------------------------------------
  // Content autopilot — default-ON daily drafting (safe automation:
  // research → ideas → scored drafts; nothing publishes without you).
  // Its drafts land in the pipeline tagged 'autopilot' and surface in
  // the gallery below with their born-score badges; the status line
  // exposes the one control that matters: Pause / Resume.
  // -------------------------------------------------------------------
  const [automationRecords, setAutomationRecords] = useState<AutomationRecord[]>([]);
  useEffect(() => {
    // Registers the default-enabled record (creation-only — never
    // overrides a Pause) so Pause/Resume always has a record to flip.
    ensureDefaultAutomations().catch(() => undefined);
    return subscribeAutomations(setAutomationRecords);
  }, []);
  const autopilotEnabled =
    automationRecords.find((a) => a.id === CONTENT_AUTOPILOT_ID)?.enabled ?? true;
  const toggleAutopilot = async () => {
    if (autopilotEnabled) await pauseAutomation(CONTENT_AUTOPILOT_ID);
    else await enableAutomation(CONTENT_AUTOPILOT_ID);
  };
  /** Autopilot drafts still in the Idea/Draft lanes, newest first. */
  const autopilotItems = useMemo(
    () =>
      contentItems
        .filter((i) => isAutopilotItem(i) && (i.status === 'idea' || i.status === 'draft'))
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [contentItems],
  );

  // Multi-format generation (Ask #6): one brief -> four platform formats via
  // the real Claude worker, each auto-scored. Only offered when the worker
  // is actually configured — we never fake AI output.
  const workerReady = argoWorker.isConfigured();
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState<FormatResult[]>([]);

  // CRM-grounded per-format generation (auditor findings #1/#11): true
  // format tabs, voice- and CRM-grounded prompts through the REAL worker
  // seam, deterministic fallback when it isn't configured — never dead.
  const [activeFormat, setActiveFormat] = useState<string>('linkedinPost');
  const [groundedBrief, setGroundedBrief] = useState('');
  const [grounded, setGrounded] = useState<Record<string, GroundedState>>({});

  // Post Performance Ledger (auditor findings: no performance capture, no
  // per-author signal derivation). Records are built by the PURE engine in
  // lib/content/postPerformance and held in view memory for the session.
  const [loggedPosts, setLoggedPosts] = useState<LoggedPost[]>([]);
  const [perfTab, setPerfTab] = useState<'log' | 'insights'>('log');

  const handleLogPost = (post: LoggedPost) => setLoggedPosts((prev) => [...prev, post]);

  // Inline metric edits rebuild the record through createLoggedPost so the
  // derived features (hook/topic/length) always stay true to the text.
  const handleUpdateMetrics = (id: string, metrics: PostMetrics) =>
    setLoggedPosts((prev) =>
      prev.map((p) =>
        p.id === id
          ? createLoggedPost({
              id: p.id,
              text: p.text,
              format: p.format,
              postedAtHour: p.postedAtHour,
              metrics,
            })
          : p,
      ),
    );

  const handleGenerateAll = async () => {
    const brief = topic.trim();
    if (!brief || !workerReady || generating) return;
    const formats = contentFormats();
    setGenerating(true);
    setResults(formats.map((f) => ({ format: f, status: 'loading' as const, text: '' })));
    // Fire all four in parallel; each card resolves/fails independently so
    // one slow or failed format never blocks the rest.
    await Promise.all(
      formats.map(async (f, i) => {
        try {
          const out = await argoWorker.runAgent(
            {
              id: `content-engine-${f.id}`,
              name: 'Content Engine',
              template: 'content-writer',
              persona: `${f.label} writer`,
            },
            { prompt: buildGenerationPrompt(brief, f) },
          );
          setResults((prev) =>
            prev.map((r, j) => (j === i ? { ...r, status: 'done', text: out.text.trim() } : r)),
          );
        } catch (err) {
          setResults((prev) =>
            prev.map((r, j) =>
              j === i
                ? {
                    ...r,
                    status: 'error',
                    error: err instanceof Error ? err.message : 'Generation failed.',
                  }
                : r,
            ),
          );
        }
      }),
    );
    setGenerating(false);
  };

  // Load personas + targetable contacts on mount. Targets are prospects AND
  // customers: the header promises "prospect/customer", and a one-pager or
  // follow-up aimed at an existing customer is a real need (expansion/renewal),
  // so we don't restrict targeting to net-new prospects. Prospects are listed
  // first (the primary outreach audience), then customers.
  useEffect(() => {
    bootstrapPeopleIfEmpty().catch(() => undefined);
    (async () => {
      const allPersonas = await listByCategory('persona');
      setPersonas(allPersonas);
      const [prospects, customers] = await Promise.all([
        listByCategory('prospect'),
        listByCategory('customer'),
      ]);
      setTargets([...prospects, ...customers]);
    })();
  }, []);

  // Generate draft whenever asset type or selection changes.
  const selectedPersonaRecord = useMemo(
    () => personas.find((p) => p.id === selectedPersona),
    [personas, selectedPersona],
  );

  const selectedTargetRecord = useMemo(
    () => targets.find((p) => p.id === selectedTarget),
    [targets, selectedTarget],
  );

  useEffect(() => {
    setDraft(
      generateDraft(assetType, senderName, selectedPersonaRecord, selectedTargetRecord),
    );
  }, [assetType, selectedPersonaRecord, selectedTargetRecord, senderName]);

  // The voice every grounded asset is written in. No brand doc is stored
  // yet, so this is the honest house-default profile (the module accepts a
  // real brand doc + Company Brain facts when an account supplies them).
  const voice = useMemo(() => buildVoiceProfile(undefined, undefined), []);

  // Grounding context from the SELECTED persona + target: merge fields are
  // resolved through the shipped registry (lib/crm/mergeFields) — tokens
  // that can't resolve are dropped, never faked.
  const groundedCtx = useMemo<GenContext>(() => {
    const mergeCtx = {
      account: selectedTargetRecord?.company
        ? { name: selectedTargetRecord.company }
        : null,
      contacts: selectedTargetRecord
        ? [{ name: selectedTargetRecord.name, title: selectedTargetRecord.title }]
        : null,
      owner: user?.displayName ? { name: user.displayName } : null,
    };
    const { resolved, missing } = resolveMerge(
      '{{account.name}} {{contact.name}} {{contact.title}} {{owner.name}}',
      mergeCtx,
    );
    for (const token of missing) delete resolved[token];
    return {
      account: mergeCtx.account ?? undefined,
      person: selectedTargetRecord
        ? {
            name: selectedTargetRecord.name,
            title: selectedTargetRecord.title,
            company: selectedTargetRecord.company,
          }
        : undefined,
      persona: selectedPersonaRecord
        ? {
            archetype: selectedPersonaRecord.archetype,
            summary: selectedPersonaRecord.summary,
            profile: selectedPersonaRecord.profile,
          }
        : undefined,
      voice,
      mergeFields: resolved,
    };
  }, [selectedTargetRecord, selectedPersonaRecord, user?.displayName, voice]);

  const handleGroundedGenerate = async (fmt: string) => {
    const brief = groundedBrief.trim();
    if (!brief) return;
    setGrounded((prev) => ({ ...prev, [fmt]: { status: 'loading' } }));
    try {
      // Real worker when configured; the module falls back deterministically
      // (with honest provenance) when it's absent or fails.
      const asset = await generateAsset(brief, fmt, groundedCtx, {
        worker: workerReady ? argoWorker : undefined,
      });
      setGrounded((prev) => ({
        ...prev,
        [fmt]: {
          status: 'done',
          asset,
          validation: validateAsset(asset.text, FORMAT_SPECS[fmt]),
        },
      }));
    } catch (err) {
      setGrounded((prev) => ({
        ...prev,
        [fmt]: {
          status: 'error',
          error: err instanceof Error ? err.message : 'Generation failed.',
        },
      }));
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is unavailable.
    }
  };

  // -----------------------------------------------------------------------
  // HOME — a clean landing: analytics strip, the five-type picker, the
  // templates gallery front + center, and the scheduled-posts week. Every
  // dense tool waits behind a chosen type.
  // -----------------------------------------------------------------------
  if (activeType === null) {
    // THE INVERSION: prepped drafts exist → open on finished work. The
    // picker only leads when the Brain can't ground anything (or the
    // operator explicitly asked for '+ New').
    // Autopilot drafts count as finished work too — the gallery stays the
    // landing even after every prepped slot has been minted into an item.
    const galleryMode = (prepped.length > 0 || autopilotItems.length > 0) && !showPicker;
    return (
      <div className="app-container" style={{ display: 'grid', gap: 16 }}>
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <span className="t-eyebrow">Content</span>
            <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
              {galleryMode ? 'Argo prepped these' : 'Content Engine'}
            </h1>
            <p className="t-meta" style={{ margin: '6px 0 0' }}>
              {galleryMode
                ? 'Ready-to-review drafts composed from your Brain and real records — open one, score it, ship it.'
                : "Pick what you're making — the workspace shows only the tools that type needs."}
            </p>
          </div>
          {galleryMode && (
            <button
              onClick={() => setShowPicker(true)}
              className="glass-btn"
              title="Start something from scratch — the type picker"
              style={{ padding: '6px 12px', fontSize: 12, flexShrink: 0 }}
            >
              <PlusCircle size={13} />
              New
            </button>
          )}
        </header>

        {/* Analytics strip — honest counts + avg score at a glance. */}
        <GlassPanel style={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <BarChart3 size={14} style={{ color: C.blueElectric, flexShrink: 0 }} />
            {homeAnalytics.map((tile) => (
              <span
                key={tile.key}
                className="glass-pill"
                title={tile.hint}
                style={{ fontSize: 11, padding: '4px 10px', color: C.text }}
              >
                <span style={{ color: C.textMuted, marginRight: 5 }}>{tile.label}</span>
                <strong>{tile.value}</strong>
              </span>
            ))}
          </div>
        </GlassPanel>

        {/* 0 — Argo-prepped drafts: the inverted cold-start. Cards are
            grounded in real Brain facts / records (see each card's
            provenance line) and stay honest drafts — publishing still
            lives behind the born-scored gate inside the workspace. */}
        {galleryMode && prepped.length > 0 && (
          <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Sparkles size={15} style={{ color: C.magentaSoft }} />
              <span className="t-section-title" style={{ color: C.text }}>
                Argo prepped these
              </span>
              <span
                className="glass-pill"
                title="Deterministic drafts composed from your Company Brain + real records — no network calls, nothing invented. They stay drafts until you score and ship them."
                style={{
                  marginLeft: 'auto',
                  fontSize: 10,
                  padding: '3px 10px',
                  color: C.textMuted,
                  background: 'rgba(180,188,210,0.15)',
                }}
              >
                Argo-prepped drafts · deterministic
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 10,
              }}
            >
              {prepped.map((spec) => {
                const meta = contentTypeMeta(spec.type);
                const Icon = STUDIO_TYPE_ICONS[spec.type];
                const preview = spec.body.split('\n').filter(Boolean).slice(0, 3).join(' ');
                return (
                  <div
                    key={spec.slot}
                    style={{
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      padding: 12,
                      background: 'rgba(255,255,255,0.03)',
                      display: 'grid',
                      gap: 8,
                      alignContent: 'start',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon size={13} style={{ color: C.magentaSoft, flexShrink: 0 }} />
                      <span
                        className="glass-pill"
                        style={{ fontSize: 9, padding: '1px 7px', color: C.magentaSoft }}
                      >
                        {meta.label}
                      </span>
                      <button
                        onClick={() => dismissPrepped(spec.slot)}
                        className="glass-btn"
                        title="Dismiss this draft"
                        aria-label={`Dismiss ${spec.title}`}
                        style={{ marginLeft: 'auto', padding: '2px 6px' }}
                      >
                        <XDismiss size={11} style={{ color: C.textMuted }} />
                      </button>
                    </div>
                    <div className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                      {spec.title}
                    </div>
                    <div
                      className="t-meta"
                      style={{
                        color: C.textDim,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {preview}
                    </div>
                    <div
                      className="t-meta"
                      title={spec.grounding.join('\n')}
                      style={{ color: C.textMuted, fontSize: 10 }}
                    >
                      Grounded in {spec.grounding.length} real record
                      {spec.grounding.length === 1 ? '' : 's'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => void openPrepped(spec, { score: true })}
                        className="glass-btn glass-btn--primary"
                        style={{ padding: '4px 10px', fontSize: 11 }}
                      >
                        <Gauge size={11} />
                        Open &amp; score
                      </button>
                      <button
                        onClick={() => void openPrepped(spec, { score: false })}
                        className="glass-btn"
                        title="Open unscored — edit first, score on save"
                        style={{ padding: '4px 10px', fontSize: 11 }}
                      >
                        <Pencil size={11} />
                        Edit
                      </button>
                      <button
                        onClick={() => regeneratePrepped(spec.slot)}
                        className="glass-btn"
                        title="Re-derive this draft (deterministic variant)"
                        style={{ padding: '4px 10px', fontSize: 11 }}
                      >
                        <RotateCcw size={11} />
                        Regenerate
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassPanel>
        )}

        {/* 0.5 — Content autopilot: research → ideas → scored drafts,
            daily, on its own. Drafting is SAFE automation — nothing
            publishes without approval — so the only control is Pause.
            Its landed drafts render here with their born-score badges
            (Simulated stays labeled — never laundered into a real read). */}
        {galleryMode && (
          <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Zap
                size={15}
                style={{ color: autopilotEnabled ? C.green : C.textMuted, flexShrink: 0 }}
              />
              <span className="t-card-title" style={{ color: C.text }}>
                {autopilotEnabled
                  ? 'Autopilot drafts daily — research → ideas → scored drafts. Nothing publishes without you.'
                  : 'Autopilot paused — no new drafts until you resume.'}
              </span>
              <button
                onClick={() => void toggleAutopilot()}
                className="glass-btn"
                title={
                  autopilotEnabled
                    ? 'Pause the daily drafting run — existing drafts stay put'
                    : 'Resume the daily drafting run'
                }
                style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11 }}
              >
                {autopilotEnabled ? 'Pause' : 'Resume'}
              </button>
            </div>
            {autopilotItems.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 10,
                }}
              >
                {autopilotItems.map((item) => {
                  const meta = contentTypeMeta(item.type);
                  const Icon = STUDIO_TYPE_ICONS[item.type];
                  const scoreColor =
                    item.score !== null && item.score >= 80 ? C.green : C.orange;
                  return (
                    <button
                      key={item.id}
                      onClick={() => openType(item.type)}
                      title={`Open the ${meta.label} workspace`}
                      style={{
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10,
                        padding: 12,
                        background: 'rgba(255,255,255,0.03)',
                        display: 'grid',
                        gap: 8,
                        alignContent: 'start',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon size={13} style={{ color: C.green, flexShrink: 0 }} />
                        <span
                          className="glass-pill"
                          style={{ fontSize: 9, padding: '1px 7px', color: C.green }}
                        >
                          Autopilot · {meta.label}
                        </span>
                        {item.score !== null && (
                          <span
                            className="glass-pill"
                            title={
                              item.scoreSimulated
                                ? 'Born-scored with the honest local simulation — connect the engine for a real AQI read'
                                : 'Born-scored by the real AQI engine'
                            }
                            style={{
                              marginLeft: 'auto',
                              fontSize: 10,
                              padding: '2px 8px',
                              background: `${scoreColor}22`,
                              color: scoreColor,
                              boxShadow: `inset 0 0 0 1px ${scoreColor}55`,
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {item.score}/100{item.scoreSimulated ? ' · Simulated' : ''}
                          </span>
                        )}
                      </div>
                      <div className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                        {item.title}
                      </div>
                      <div
                        className="t-meta"
                        style={{
                          color: C.textDim,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.body.split('\n').filter(Boolean).slice(0, 2).join(' ')}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </GlassPanel>
        )}

        {!galleryMode && (
        <>
        {prepped.length > 0 ? (
          <button
            onClick={() => setShowPicker(false)}
            className="glass-btn"
            style={{ justifySelf: 'start', padding: '4px 10px', fontSize: 11 }}
          >
            <ArrowLeft size={12} />
            Back to prepped drafts
          </button>
        ) : (
          brainFacts.length === 0 && (
            <GlassPanel
              style={{
                padding: 12,
                background: 'rgba(180,188,210,0.06)',
                border: '1px solid rgba(180,188,210,0.18)',
              }}
            >
              <p className="t-meta" style={{ margin: 0, display: 'flex', gap: 6 }}>
                <Lightbulb size={13} style={{ color: C.orange, flexShrink: 0, marginTop: 1 }} />
                <span>
                  Set up your Brain to get overnight drafts — once the Company
                  Brain holds real facts, this page opens on Argo-prepped
                  drafts instead of a blank picker.
                </span>
              </p>
            </GlassPanel>
          )
        )}

        {/* 1 — Type picker: the door into every workspace. */}
        <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
          <div className="t-section-title" style={{ color: C.text }}>
            What are you creating today?
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              gap: 10,
            }}
          >
            {CONTENT_TYPES.map((t) => {
              const Icon = STUDIO_TYPE_ICONS[t.id];
              const count = homeSummary.byType[t.id];
              return (
                <button
                  key={t.id}
                  onClick={() => openType(t.id)}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    padding: 14,
                    cursor: 'pointer',
                    display: 'grid',
                    gap: 6,
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon size={16} style={{ color: C.magenta }} />
                    <span className="t-card-title" style={{ color: C.text }}>
                      {t.label}
                    </span>
                    {count > 0 && (
                      <span
                        className="glass-pill"
                        style={{ marginLeft: 'auto', fontSize: 10, padding: '2px 8px' }}
                      >
                        {count}
                      </span>
                    )}
                  </div>
                  <span className="t-meta" style={{ color: C.textDim }}>
                    {t.description}
                  </span>
                  <span className="t-meta" style={{ color: C.magentaSoft }}>
                    Open workspace →
                  </span>
                </button>
              );
            })}
          </div>
        </GlassPanel>

        {/* 2 — Templates gallery, front + center: one click mints a Draft
            and drops you straight into the right workspace. */}
        <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={15} style={{ color: C.magentaSoft }} />
            <span className="t-section-title" style={{ color: C.text }}>
              Start from a template
            </span>
            <span className="t-meta" style={{ color: C.textDim }}>
              one click → scored draft → workspace
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 10,
            }}
          >
            {CONTENT_TEMPLATES.map((tpl) => {
              const meta = contentTypeMeta(tpl.type);
              const Icon = STUDIO_TYPE_ICONS[tpl.type];
              return (
                <div
                  key={tpl.id}
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10,
                    padding: 12,
                    background: 'rgba(255,255,255,0.03)',
                    display: 'grid',
                    gap: 6,
                    alignContent: 'start',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon size={13} style={{ color: C.magentaSoft, flexShrink: 0 }} />
                    <span className="t-card-title" style={{ fontSize: 12, color: C.text }}>
                      {tpl.name}
                    </span>
                    <span
                      className="glass-pill"
                      style={{ marginLeft: 'auto', fontSize: 9, padding: '1px 7px', color: C.textMuted }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <span className="t-meta" style={{ color: C.textDim }}>
                    {tpl.description}
                  </span>
                  <button
                    onClick={() => void useHomeTemplate(tpl)}
                    className="glass-btn glass-btn--primary"
                    style={{ justifySelf: 'start', padding: '4px 10px', fontSize: 11 }}
                  >
                    <PlusCircle size={11} />
                    Use template
                  </button>
                </div>
              );
            })}
          </div>
        </GlassPanel>
        </>
        )}

        {/* 3 — Scheduled posts: the simple next-7-days calendar. */}
        <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarClock size={15} style={{ color: C.orange }} />
            <span className="t-section-title" style={{ color: C.text }}>
              Scheduled this week
            </span>
            <span className="t-meta" style={{ color: C.textDim }}>
              from the pipeline's Scheduled column
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 8,
            }}
          >
            {scheduleDays.map((day) => (
              <div
                key={day.key}
                style={{
                  border: day.isToday
                    ? '1px solid rgba(255,0,255,0.35)'
                    : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  padding: 8,
                  background: day.isToday ? 'rgba(255,0,255,0.06)' : 'rgba(255,255,255,0.02)',
                  display: 'grid',
                  gap: 6,
                  alignContent: 'start',
                  minHeight: 72,
                }}
              >
                <div
                  className="t-meta"
                  style={{ color: day.isToday ? C.magentaSoft : C.textMuted, fontWeight: 600 }}
                >
                  {day.label}
                  {day.isToday ? ' · today' : ''}
                </div>
                {day.items.length === 0 ? (
                  <div className="t-meta" style={{ color: C.textDim }}>
                    —
                  </div>
                ) : (
                  day.items.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => openType(it.type)}
                      title={`${it.title} — open the ${contentTypeMeta(it.type).label} workspace`}
                      className="glass-pill"
                      style={{
                        fontSize: 10,
                        padding: '3px 8px',
                        color: C.orange,
                        background: 'rgba(255,166,0,0.12)',
                        cursor: 'pointer',
                        border: 'none',
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.title}
                    </button>
                  ))
                )}
              </div>
            ))}
          </div>
          {homeSummary.byStatus.scheduled === 0 && (
            <p className="t-meta" style={{ margin: 0, color: C.textDim }}>
              Nothing scheduled yet — move a draft to “Scheduled” inside any
              workspace and give it a date to see it here.
            </p>
          )}
        </GlassPanel>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // WORKSPACE — one chosen type; only its relevant tools render below.
  // -----------------------------------------------------------------------
  const activeMeta = contentTypeMeta(activeType);
  const tools = toolsForType(activeType);
  const ActiveIcon = STUDIO_TYPE_ICONS[activeType];
  const latestOfType = contentItems.find((i) => i.type === activeType);

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      <header style={{ display: 'grid', gap: 8 }}>
        <button
          onClick={() => setActiveType(null)}
          className="glass-btn"
          style={{ justifySelf: 'start', padding: '4px 10px', fontSize: 11 }}
        >
          <ArrowLeft size={12} />
          All content types
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ActiveIcon size={20} style={{ color: C.magenta }} />
          <h1 className="t-page-title" style={{ margin: 0 }}>
            {activeMeta.label}
          </h1>
        </div>
        <p className="t-meta" style={{ margin: 0 }}>
          {activeMeta.description} Showing only this type's tools.
        </p>
      </header>

      {/* The type's board: templates for THIS type + its Idea → Published
          pipeline. Pure helpers: lib/content/pipeline. */}
      <ContentStudioPanel lockedType={activeType} />

      {/* Honest image-gen affordance: POSTs to /generate-image (backend
          wired separately by the operator) — never fakes a picture. */}
      {tools.includes('image-gen') && (
        <GenerateImageButton type={activeType} title={latestOfType?.title ?? ''} />
      )}

      {/* Live SEO editor — blogs only: side-by-side score-as-you-type editor
          over the deterministic seoScore engine. */}
      {tools.includes('seo-editor') && <LiveSeoEditorPanel />}

      {/* Multi-format generation: one brief -> LinkedIn post, X one-liner,
          X thread opener, and a short email — real Claude via the worker,
          each auto-scored with the shipped scorer. */}
      {tools.includes('multi-format') && (
      <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={16} style={{ color: C.magenta }} />
          <span className="t-section-title" style={{ color: C.text }}>
            Multi-format generation
          </span>
          <span className="t-meta" style={{ color: C.textDim }}>
            one brief · four platform formats · auto-scored
          </span>
        </div>

        {workerReady ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleGenerateAll();
              }}
              placeholder="Brief or topic, e.g. how agents cut our ticket triage time 60%"
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                padding: '10px 12px',
                color: C.text,
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              onClick={() => void handleGenerateAll()}
              disabled={generating || !topic.trim()}
              className="glass-btn glass-btn--primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: generating || !topic.trim() ? 0.5 : 1,
                cursor: generating || !topic.trim() ? 'default' : 'pointer',
              }}
            >
              {generating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {generating ? 'Generating…' : 'Generate all formats'}
            </button>
          </div>
        ) : (
          <div className="t-meta" style={{ color: C.textMuted, display: 'flex', gap: 6 }}>
            <AlertTriangle size={13} style={{ color: C.orange, flexShrink: 0, marginTop: 1 }} />
            <span>
              Connect AI to generate — set VITE_AGENT_API_URL to your agent worker to produce
              real multi-format drafts. The deterministic composer below still works.
            </span>
          </div>
        )}

        {results.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {results.map((r) => (
              <FormatResultCard key={r.format.id} result={r} />
            ))}
          </div>
        )}
      </GlassPanel>
      )}

      {/* CRM-grounded generation: real format tabs with true per-format
          constraints, voice + persona + merge-field grounding, provenance
          and format validation on every draft. */}
      {tools.includes('grounded') && (
      <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Wand2 size={16} style={{ color: C.blueElectric }} />
          <span className="t-section-title" style={{ color: C.text }}>
            Grounded generation
          </span>
          <span className="t-meta" style={{ color: C.textDim }}>
            per-format constraints · live CRM facts · honest provenance
          </span>
          <span
            className="glass-pill"
            title="Every draft is written in this voice profile"
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              padding: '3px 10px',
              color: C.magentaSoft,
              background: `${C.magentaSoft}18`,
            }}
          >
            voice: {voice.name}
          </span>
        </div>

        {/* Format tabs — one per FORMAT_SPECS entry, each a real spec. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="tablist">
          {Object.values(FORMAT_SPECS).map((spec) => {
            const isActive = activeFormat === spec.format;
            return (
              <button
                key={spec.format}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveFormat(spec.format)}
                className="glass-pill"
                style={{
                  fontSize: 12,
                  padding: '5px 12px',
                  cursor: 'pointer',
                  border: 'none',
                  color: isActive ? C.text : C.textMuted,
                  background: isActive ? 'rgba(255,0,255,0.14)' : 'rgba(255,255,255,0.05)',
                  boxShadow: isActive ? 'inset 0 0 0 1px rgba(255,0,255,0.35)' : 'none',
                }}
              >
                {FORMAT_LABELS[spec.format] ?? spec.format}
              </button>
            );
          })}
        </div>

        {/* Active format's hard constraints, straight from the spec. */}
        <div className="t-meta" style={{ color: C.textDim }}>
          {FORMAT_SPECS[activeFormat].structure.join(' → ')}
          {FORMAT_SPECS[activeFormat].charLimit !== undefined &&
            ` · ${FORMAT_SPECS[activeFormat].charLimit} chars${
              activeFormat === 'xThread' ? ' / post' : ''
            }`}
          {FORMAT_SPECS[activeFormat].ctaRequired && ' · CTA required'}
          {FORMAT_SPECS[activeFormat].hookFirst && ' · hook-first'}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={groundedBrief}
            onChange={(e) => setGroundedBrief(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleGroundedGenerate(activeFormat);
            }}
            placeholder="Brief, e.g. why grounded agents beat template tools for RevOps teams"
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              padding: '10px 12px',
              color: C.text,
              fontSize: 13,
              outline: 'none',
            }}
          />
          <button
            onClick={() => void handleGroundedGenerate(activeFormat)}
            disabled={!groundedBrief.trim() || grounded[activeFormat]?.status === 'loading'}
            className="glass-btn glass-btn--primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity:
                !groundedBrief.trim() || grounded[activeFormat]?.status === 'loading' ? 0.5 : 1,
              cursor:
                !groundedBrief.trim() || grounded[activeFormat]?.status === 'loading'
                  ? 'default'
                  : 'pointer',
            }}
          >
            {grounded[activeFormat]?.status === 'loading' ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Wand2 size={14} />
            )}
            Generate
          </button>
        </div>

        {!workerReady && (
          <div className="t-meta" style={{ color: C.textMuted }}>
            Worker not configured — drafts use the deterministic fallback (labelled honestly
            below). Set VITE_AGENT_API_URL for live Claude generation.
          </div>
        )}

        <GroundedResultCard state={grounded[activeFormat]} spec={FORMAT_SPECS[activeFormat]} />
      </GlassPanel>
      )}

      {/* Closed score→rewrite→diff→variations loop for the active grounded
          draft: live per-dimension meter, targeted "Fix this" rewrites shown
          as approvable/undoable diffs, and scored lineage-linked variations. */}
      {tools.includes('grounded') &&
        grounded[activeFormat]?.status === 'done' &&
        grounded[activeFormat]?.asset && (
        <ScoreRewritePanel
          text={grounded[activeFormat].asset.text}
          brief={groundedBrief}
          format={activeFormat}
          ctx={groundedCtx}
          workerReady={workerReady}
          onApply={(next) =>
            setGrounded((prev) => {
              const cur = prev[activeFormat];
              if (!cur?.asset) return prev;
              return {
                ...prev,
                [activeFormat]: {
                  ...cur,
                  asset: { ...cur.asset, text: next },
                  validation: validateAsset(next, FORMAT_SPECS[activeFormat]),
                },
              };
            })
          }
        />
      )}

      {/* History-shaped generation: prompts blended from THIS author's
          What-Works signals + retrieved playbook rules (cold start leans
          100% playbook), generated through the real worker seam with a
          deterministic fallback, provenance receipts naming exactly which
          signals/rules shaped the draft, a calibrated score with one-click
          "rewrite lowest dimension", and predicted-vs-actual backtesting. */}
      {tools.includes('history') && (
        <HistoryShapedPanel
          posts={loggedPosts}
          format={activeFormat}
          workerReady={workerReady}
        />
      )}

      {/* Governed weekly learning loop: the Claude ritual as one scheduled,
          human-approved, reversible, outcome-verified mission over the pure
          engines in lib/content/learningLoop + lib/content/authorProfile. */}
      {tools.includes('learning-loop') && (
        <WeeklyLoopPanel
          posts={loggedPosts}
          ideaSeed={groundedBrief || topic}
          workerReady={workerReady}
        />
      )}

      {/* Governed calendar — approvable, sealed, reversible scheduling of the
          currently-composed asset. Every slot routes through a human approval
          diff (pure engine: lib/content/schedule); nothing publishes directly. */}
      {tools.includes('governed-calendar') && (
        <GovernedCalendarPanel
          asset={{
            id: selectedTarget ? `${assetType}-${selectedTarget}` : assetType,
            title: ASSET_TYPES.find((a) => a.id === assetType)?.name ?? assetType,
          }}
          brief={topic || groundedBrief}
        />
      )}

      {/* Post Performance Ledger + per-author What-Works signals: the Log /
          Insights loop over the pure engines (lib/content/postPerformance +
          lib/content/whatWorks). Signals show lift, honest n, and evidence. */}
      {tools.includes('performance') && (
      <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <BarChart3 size={16} style={{ color: C.blueElectric, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="t-section-title" style={{ color: C.text }}>
              What works for YOU
            </div>
            <div className="t-meta" style={{ margin: '2px 0 0', color: C.textDim }}>
              Log real post performance → derive per-author signals with sample-size honesty.
            </div>
          </div>
          <div role="tablist" style={{ display: 'flex', gap: 6 }}>
            {(
              [
                ['log', 'Log'],
                ['insights', 'Insights'],
              ] as const
            ).map(([tab, label]) => {
              const isActive = perfTab === tab;
              return (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setPerfTab(tab)}
                  className="glass-pill"
                  style={{
                    fontSize: 12,
                    padding: '5px 12px',
                    cursor: 'pointer',
                    border: 'none',
                    color: isActive ? C.text : C.textMuted,
                    background: isActive ? 'rgba(255,0,255,0.14)' : 'rgba(255,255,255,0.05)',
                    boxShadow: isActive ? 'inset 0 0 0 1px rgba(255,0,255,0.35)' : 'none',
                  }}
                >
                  {label}
                  {tab === 'log' && loggedPosts.length > 0 ? ` · ${loggedPosts.length}` : ''}
                </button>
              );
            })}
          </div>
        </div>

        {perfTab === 'log' ? (
          <PostPerformancePanel
            posts={loggedPosts}
            onLog={handleLogPost}
            onUpdateMetrics={handleUpdateMetrics}
            currentDraft={grounded[activeFormat]?.asset?.text || draft}
          />
        ) : (
          <WhatWorksCards posts={loggedPosts} />
        )}
      </GlassPanel>
      )}

      {/* Deterministic sales composer — persona/target-personalized drafts. */}
      {tools.includes('sales-composer') && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Left: composer */}
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Asset type picker */}
          <GlassPanel style={{ padding: 16 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Asset Type
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {ASSET_TYPES.map((at) => {
                const Icon = at.icon;
                const isActive = assetType === at.id;
                return (
                  <button
                    key={at.id}
                    onClick={() => {
                      setAssetType(at.id);
                    }}
                    className="glass-row"
                    style={{
                      background: isActive
                        ? 'rgba(255,0,255,0.1)'
                        : 'transparent',
                      border: isActive
                        ? '1px solid rgba(255,0,255,0.3)'
                        : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 10,
                      padding: 12,
                      cursor: 'pointer',
                      gap: 10,
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Icon
                      size={18}
                      style={{
                        color: isActive ? C.magenta : C.textMuted,
                        marginTop: 2,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ textAlign: 'left' }}>
                      <div
                        className="t-card-title"
                        style={{ color: isActive ? '#fff' : C.text }}
                      >
                        {at.name}
                      </div>
                      <div
                        className="t-meta"
                        style={{
                          margin: '2px 0 0',
                          color: isActive ? C.textMuted : C.textDim,
                        }}
                      >
                        {at.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </GlassPanel>

          {/* Persona picker */}
          <GlassPanel style={{ padding: 16 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Target Persona (optional)
            </div>
            {personas.length === 0 ? (
              <p className="t-meta" style={{ margin: 0 }}>
                No personas defined yet. Create one in Contacts to personalize drafts.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                <button
                  onClick={() => setSelectedPersona(null)}
                  className="glass-btn"
                  style={{
                    background:
                      selectedPersona === null
                        ? 'rgba(255,0,255,0.14)'
                        : 'transparent',
                    color:
                      selectedPersona === null ? '#fff' : C.textMuted,
                    boxShadow:
                      selectedPersona === null
                        ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                        : undefined,
                    justifyContent: 'flex-start',
                  }}
                >
                  <Users size={13} />
                  Generic persona
                </button>
                {personas.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPersona(p.id)}
                    className="glass-btn"
                    style={{
                      background:
                        selectedPersona === p.id
                          ? 'rgba(255,0,255,0.14)'
                          : 'transparent',
                      color:
                        selectedPersona === p.id ? '#fff' : C.text,
                      boxShadow:
                        selectedPersona === p.id
                          ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                          : undefined,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>
                      <div className="t-card-title">{p.name}</div>
                      <div className="t-meta" style={{ margin: '2px 0 0' }}>
                        {p.profile?.role || 'Unknown role'}
                      </div>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </GlassPanel>

          {/* Target picker — prospects + customers */}
          <GlassPanel style={{ padding: 16 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Target Contact (optional)
            </div>
            {targets.length === 0 ? (
              <p className="t-meta" style={{ margin: 0 }}>
                No prospects or customers yet. Add contacts in the People view to personalize.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                <button
                  onClick={() => setSelectedTarget(null)}
                  className="glass-btn"
                  style={{
                    background:
                      selectedTarget === null
                        ? 'rgba(255,0,255,0.14)'
                        : 'transparent',
                    color:
                      selectedTarget === null ? '#fff' : C.textMuted,
                    boxShadow:
                      selectedTarget === null
                        ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                        : undefined,
                    justifyContent: 'flex-start',
                  }}
                >
                  <Users size={13} />
                  Generic contact
                </button>
                {targets.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedTarget(p.id)}
                    className="glass-btn"
                    style={{
                      background:
                        selectedTarget === p.id
                          ? 'rgba(255,0,255,0.14)'
                          : 'transparent',
                      color:
                        selectedTarget === p.id ? '#fff' : C.text,
                      boxShadow:
                        selectedTarget === p.id
                          ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                          : undefined,
                      justifyContent: 'flex-start',
                    }}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>
                      <div
                        className="t-card-title"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        {p.name}
                        {/* Honest category chip so customers don't read as prospects. */}
                        {p.category === 'customer' && (
                          <span
                            className="glass-pill"
                            style={{
                              fontSize: 9,
                              padding: '1px 6px',
                              background: 'rgba(34,197,94,0.14)',
                              color: C.green,
                            }}
                          >
                            Customer
                          </span>
                        )}
                      </div>
                      <div className="t-meta" style={{ margin: '2px 0 0' }}>
                        {p.company}
                        {p.title ? ` - ${p.title}` : ''}
                      </div>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </GlassPanel>
        </div>

        {/* Right: draft output */}
        <div style={{ display: 'grid', gap: 16 }}>
          <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div className="t-eyebrow">Draft</div>
                <div className="t-card-title" style={{ margin: '4px 0 0' }}>
                  {ASSET_TYPES.find((a) => a.id === assetType)?.name}
                </div>
              </div>
              <button
                onClick={handleCopy}
                className="glass-btn"
                style={{
                  background: copied
                    ? 'rgba(34,197,94,0.14)'
                    : 'transparent',
                  color: copied ? C.green : C.text,
                  boxShadow: copied
                    ? 'inset 0 0 0 1px rgba(34,197,94,0.45)'
                    : undefined,
                }}
              >
                {copied ? (
                  <>
                    <Check size={13} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={13} /> Copy
                  </>
                )}
              </button>
            </div>

            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="glass-input"
              style={{
                minHeight: 360,
                fontFamily: 'monospace',
                fontSize: 12,
                padding: 12,
                resize: 'vertical',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: C.text,
              }}
            />
          </GlassPanel>

          {/* Best-practice score — deterministic heuristic grade of the
              visible draft, thin over lib/crm/contentScore. LinkedIn posts
              are graded against LinkedIn length norms; everything else
              against X's tighter band. */}
          <DraftScoreCard
            text={draft}
            platform={assetType === 'linkedin' ? 'linkedin' : 'x'}
          />

          {/* AQI quality scoring — REAL scoring/fixing via the deployed
              argoiq-engine when VITE_CONTENT_ENGINE_URL is set (proxied by
              our Netlify function, passcode never in the client), honest
              deterministic simulation otherwise ('Simulated' pill). Score
              renders the six-dimension breakdown + total dial; Fix shows a
              before → after receipt with the lift highlighted. */}
          <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
            <div className="t-eyebrow">Quality (AQI)</div>
            <AqiScorePanel
              content={draft}
              channel={AQI_CHANNEL_FOR_ASSET[assetType]}
              customer={selectedTargetRecord?.company}
              onApplyFix={setDraft}
            />
          </GlassPanel>

          {/* Honesty pill */}
          <GlassPanel
            style={{
              padding: 12,
              background: 'rgba(180,188,210,0.08)',
              border: '1px solid rgba(180,188,210,0.2)',
            }}
          >
            <div
              className="glass-pill"
              style={{
                background: 'rgba(180,188,210,0.15)',
                fontSize: 11,
                padding: '6px 10px',
                display: 'inline-block',
                marginBottom: 8,
              }}
            >
              Deterministic drafts
            </div>
            <p className="t-meta" style={{ margin: 0 }}>
              These are template-driven and personalized from your records - no
              network calls. For truly bespoke generation, connect the Claude
              worker via{' '}
              <code style={{ fontSize: 10, opacity: 0.7 }}>
                VITE_AGENT_API_URL
              </code>
              .
            </p>
          </GlassPanel>
        </div>
      </div>
      )}
    </div>
  );
}
